import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import type { SkuUsage } from '../data/analytics';
import type { ApiDetail } from '../features/catalog/model';
import type { EndpointSample } from '../data/samples/endpointSample';
import { useCatalogDetail } from '../features/catalog/queries';
import { publicTrySchemaQueryOptions } from '../features/execution/queries';
import { benchmarksQueryOptions } from '../features/usage/queries';
import { pageBenchmarksQueryOptions, pageNetworkUsageQueryOptions } from '../features/usage/pageQueries';
import { selectDisplayBenchmarks } from '../lib/benchmarks';
import { toSkuUsage } from '../lib/networkUsage';
import { ccVar } from '../lib/css';
import { EndpointHero } from './endpoint-detail/hero/EndpointHero';
import { ProvidersSection } from './endpoint-detail/ProvidersSection';
import { ScrapeRouterBand } from './endpoint-detail/scrape-router/ScrapeRouterBand';
import { CostEstimate } from './endpoint-detail/CostEstimate';
import { OtherEndpoints } from './endpoint-detail/OtherEndpoints';
import { PublicTryConsole } from './endpoint-detail/PublicTryConsole';
import { EndpointFaq } from './endpoint-detail/EndpointFaq';
import { EndpointReference } from './endpoint-detail/EndpointReference';
import { DocsReferenceLink } from './endpoint-detail/DocsReferenceLink';
import { Band } from '../components/editorial/Band';
import { Eyebrow } from '../components/Eyebrow';
import { FunnelBanner } from '../components/editorial/FunnelBanner';
import { CatalogFunnelAction } from '../features/catalog/browse/CatalogFunnelAction';
import { DashboardHeader } from '../components/DashboardHeader';
import { DashboardEndpointDetailBody } from './endpoint-detail/DashboardEndpointDetailBody';
import { useCatalogLinks } from '../components/CatalogLinks';
import { Link } from '@/components/Link';
import { m } from '@/paraglide/messages';
import { getLocale } from '@/paraglide/runtime';

/** English lowercases an action inside a heading ("Get Amazon reviews"); a
 *  translated action title is a name and keeps its casing. */
const proseAction = (action: string): string => (getLocale() === 'en' ? action.toLowerCase() : action);

interface Props {
  apiId: string;
  endpointId: string;
  /** Resolved by the route loader (SSR) so the header (H1) and content render
   *  server-side and the page is crawlable. Null = unknown platform. */
  detail: ApiDetail | null;
  /** the platform's fallback HugeIcons name, for the header's mark */
  icon: string;
  /** Which composition to render. Chosen by the route that owns the URL, never
   *  sniffed: `site` is the editorial page Google indexes, `dashboard` is the
   *  working page a signed-in customer browses. */
  presentation: 'site' | 'dashboard';
  /** a hand-written sample for an endpoint the authored copy does not cover */
  sample?: EndpointSample | null;
}

export const EndpointDetailPage = ({
  apiId,
  endpointId,
  detail: initialDetail,
  icon,
  presentation,
  sample,
}: Props) => {
  const links = useCatalogLinks();
  const queryClient = useQueryClient();
  const detail = useCatalogDetail(apiId, initialDetail, presentation === 'site' ? 'platform' : 'all');
  const scope = { sku: detail?.endpoints.find((endpoint) => endpoint.id === endpointId)?.sku ?? '' };
  const usageQuery = useQuery({
    ...pageNetworkUsageQueryOptions(queryClient, presentation === 'site' ? scope : undefined),
    select: toSkuUsage,
  });
  const usage: Record<string, SkuUsage> = usageQuery.data ?? {};
  // Unselected, because this page reads two things from the same rollup: the
  // per-SKU rows the FAQ summarizes, and the window and `lastComputedAt` the
  // reference section states. One observer, one cache entry.
  const benchmarkQuery = useQuery(presentation === 'site'
    ? pageBenchmarksQueryOptions(queryClient, scope)
    : benchmarksQueryOptions());
  const benchmarks = useMemo(
    () => (benchmarkQuery.data ? selectDisplayBenchmarks(benchmarkQuery.data) : {}),
    [benchmarkQuery.data],
  );

  const endpoint = useMemo(() => {
    if (!detail) return null;
    const found = detail.endpoints.find((e) => e.id === endpointId);
    if (!found || !sample || found.sampleResponse) return found ?? null;
    return { ...found, ...sample };
  }, [detail, endpointId, sample]);

  // The console below observes this same query, so the two share one cache
  // entry and the page makes no request the console was not already making.
  const schemaQuery = useQuery(publicTrySchemaQueryOptions(endpoint?.sku ?? ''));

  if (!detail || !endpoint) {
    if (presentation === 'dashboard') {
      return (
        <DashboardHeader
          crumb={
            <Link to={links.catalog} className="hover:text-text">
              {m.endpoint_crumb_catalog()}
            </Link>
          }
          title={m.endpoint_not_found_title()}
          description={m.endpoint_not_found_dashboard({ path: `${apiId}/${endpointId}` })}
        />
      );
    }
    return (
      <Band>
        <div className="band">
          <h1 className="display-2 text-text">{m.endpoint_not_found_title()}</h1>
          <p className="lede mt-5">
            {m.endpoint_not_found_body({ path: `${apiId}/${endpointId}` })}
          </p>
        </div>
      </Band>
    );
  }

  if (presentation === 'dashboard') {
    return (
      <DashboardEndpointDetailBody
        api={detail}
        endpoint={endpoint}
        verifiedAt={benchmarkQuery.data?.lastComputedAt}
        inputSchema={schemaQuery.data?.inputSchema}
        outputSchema={schemaQuery.data?.outputSchema}
      />
    );
  }

  const funnelHeading = (
    <ParaglideMessage
      message={m.endpoint_funnel_heading}
      inputs={{ platform: detail.name }}
      markup={{
        br: () => <br />,
        muted: ({ children }) => <span className="muted-word">{children}</span>,
      }}
    />
  );

  return (
    <div className="rhythm-tight" style={ccVar(detail.color)}>
      <EndpointHero
        api={detail}
        endpoint={endpoint}
        icon={icon}
        usage={usage[endpoint.sku]}
        benchmark={benchmarks[endpoint.sku]}
        window={benchmarkQuery.data?.window}
        outputSchema={schemaQuery.data?.outputSchema}
      />

      {endpoint.sku === 'web.scrape' ? <ScrapeRouterBand /> : null}

      <Band>
        {/* Price and providers first - it is what customers weigh before
            anything else. The table itself is the only statement of the order
            calls are tried in, so no sentence repeats it. */}
        <div className="band rule-dashed">
          <ProvidersSection endpoint={endpoint} name={detail.name} />
          <CostEstimate endpoint={endpoint} />
        </div>

        {/* How to call it - secondary to price, so it sits below. */}
        <div className="band">
          <Eyebrow>{endpoint.tryEligible ? m.endpoint_try_eyebrow() : m.endpoint_call_eyebrow()}</Eyebrow>
          <h2 className="display-3 mt-4 text-text">
            {m.endpoint_first_request_heading({ name: detail.name, action: proseAction(endpoint.action) })}
          </h2>
          <div className="window mt-8 p-4 sm:p-6">
            <PublicTryConsole
              key={`${endpoint.id}:${endpoint.sku}`}
              endpoint={endpoint}
              title={`${detail.name} ${endpoint.action}`}
              acquisition
            />
          </div>
          <DocsReferenceLink apiId={apiId} endpointId={endpoint.id} />
        </div>
      </Band>

      {/* The same request and the same fields as the console above, but as a
          real table rather than a list the console builds after it renders. The
          schemas come from the query the route prefetches from the build's
          OpenAPI projection, so every SKU has them, not only the ones carrying
          an authored example. */}
      <EndpointReference
        apiId={apiId}
        endpoint={endpoint}
        verifiedAt={benchmarkQuery.data?.lastComputedAt}
        healthWindow={benchmarkQuery.data?.window}
        inputSchema={schemaQuery.data?.inputSchema}
        outputSchema={schemaQuery.data?.outputSchema}
      />

      {/* Below the table and the console, for the reason ApiFaq sits below the
          endpoint list: the price and the call are what a reader came for. The
          passage is for the extractor that cannot read the table at all. */}
      <EndpointFaq api={detail} endpoint={endpoint} benchmarks={benchmarks} />

      <OtherEndpoints api={detail} current={endpoint} />

      <FunnelBanner
        heading={funnelHeading}
        action={<CatalogFunnelAction />}
      />
    </div>
  );
};
